import restClient from "api/restClient";
import { RestError } from "api/restClient.types";
import { Button } from "components/Button/Button";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { Callout } from "components/Form/components/Callout/Callout";
import { Form } from "components/Form/components/Form/Form";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { useCallback, useMemo, useState } from "react";
import { Field } from "react-final-form";
import { useMount } from "react-use";

export const defaultAuthValue = "cloudomation";
const defaultAuthBootstrapValue = "cloudomation bootstrap user";
const defaultAuthOption = {
    value: defaultAuthValue,
    label: "Cloudomation",
};

interface AuthMethodsFormFieldProps {
    initialValue: string;
    authOptionList: { value: string; label: string }[];
}

const AuthMethodsFormField = ({ authOptionList, initialValue }: AuthMethodsFormFieldProps) => {
    return (
        <Form.Row>
            <Field
                name="authenticationMethod"
                id="authenticationMethod"
                fieldProps={{
                    clearable: false,
                    label: "Authentication method",
                    options: {
                        list: authOptionList,
                    },
                    disabled: authOptionList.length === 1,
                }}
                initialValue={initialValue}
                component={AutocompleteFormComponent}
            />
        </Form.Row>
    );
};

export const AuthMethodsContainer = () => {
    const [authMethods, setAuthMethods] = useState<string[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<RestError | null>(null);

    const fetchAuthMethods = useCallback(async () => {
        try {
            const authMethodsResponse = await restClient.get("/auth/login");
            setAuthMethods(authMethodsResponse?.data ?? []);
            setLoadError(null);
            setIsLoaded(true);
        } catch (error) {
            const restError = error as RestError;
            setLoadError(restError);
            setIsLoaded(false);
            NotificationService.error(<RestErrorNotification error={restError} />);
        }
    }, []);

    useMount(() => {
        fetchAuthMethods();
    });

    const hasDefaultAuthValue = useMemo(
        () => authMethods.includes(defaultAuthValue),
        [authMethods]
    );

    const authOptionList = useMemo(() => {
        let optionList = [...authMethods];
        if (hasDefaultAuthValue) {
            if (authMethods.length === 1) {
                return [];
            }
        } else {
            optionList.push(defaultAuthBootstrapValue);
        }
        const mappedOptionList = optionList.map((authOption) => ({
            value: authOption === defaultAuthBootstrapValue ? defaultAuthValue : authOption,
            label: authOption.charAt(0).toUpperCase() + authOption.slice(1),
        }));
        return mappedOptionList;
    }, [authMethods, hasDefaultAuthValue]);

    const defaultValue = useMemo(() => {
        if (hasDefaultAuthValue) {
            return defaultAuthOption;
        } else {
            return authOptionList[0];
        }
    }, [hasDefaultAuthValue, authOptionList]);

    if (loadError) {
        return (
            <Form.Row>
                <Callout
                    type="error"
                    text="Could not load authentication methods. Please try again."
                    className="rootCallout"
                />
                <Button variation="link" onClick={fetchAuthMethods} label="Retry" />
            </Form.Row>
        );
    }

    return authOptionList.length && isLoaded ? (
        <AuthMethodsFormField authOptionList={authOptionList} initialValue={defaultValue.value} />
    ) : null;
};
